import { Injectable, signal, inject } from '@angular/core';
import { injectQueryClient, keepPreviousData, mutationOptions, queryOptions } from '@tanstack/angular-query-experimental';
import { Observable, firstValueFrom, from } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { BackendService } from '../services/backend.service';
import { Bucket, GroupPage, GroupSpendingTrendPoint } from '../types';
import { MonthService } from './month.service';
import {
  AccountBudget,
  GroupList,
  GroupMonthlyPace,
  GroupMonthlyReport,
  GroupSpendingTrend,
  CreateGroupRequest,
  UpdateAccountBudgetRequest,
} from '../generated-api/models';
import { groupQueryKeys } from './query-keys';

export interface BudgetBulkEditRequest {
  monthYear: string;
  groups: BudgetBulkEditRequestGroup[];
  archives: BudgetBulkEditRequestArchive[];
  unarchives: string[];
}

export interface BudgetBulkEditRequestGroup {
  clientId: string;
  id?: string;
  name: string;
  sortIndex: number;
  buckets: BudgetBulkEditRequestBucket[];
}

export interface BudgetBulkEditRequestBucket {
  clientId: string;
  id?: string;
  name: string;
  amount: number;
  type: 'INCOME' | 'BUDGET' | 'SAVINGS' | 'TRANSFERS';
  sortIndex: number;
  archive?: boolean;
  isSetup?: boolean;
}

export interface BudgetBulkEditRequestArchive {
  bucketId: string;
  transferBalanceBucketClientId?: string;
}

export interface GroupNameMutationVariables {
  name: string;
  monthYear?: string | null;
}

export interface DeleteGroupMutationVariables {
  id: string;
  monthYear?: string | null;
}

export interface UpdateMonthlyBudgetMutationVariables {
  budget: number;
  monthYear?: string | null;
}

@Injectable({ providedIn: 'root' })
export class GroupService {
  private backend = inject(BackendService);
  private month = inject(MonthService);

  private readonly queryClient = injectQueryClient();
  private readonly reorderingState = signal(false);
  readonly reorderingSignal = this.reorderingState.asReadonly();

  groupSearch = '';

  activePanel: string | boolean = false;

  get reordering(): boolean {
    return this.reorderingState();
  }

  set reordering(value: boolean) {
    this.reorderingState.set(value);
  }

  ignoreIncomeMismatchRequest$ = this.backend.request<void>({
    type: 'update',
    apiRoute: 'account/warnings/ignore/budget-income-mismatch',
  });

  groupSpendingTrendQueryOptions(monthYear = this.month.monthYearFromDate()) {
    const normalizedMonthYear = this.normalizeMonthYear(monthYear);
    return queryOptions({
      queryKey: groupQueryKeys.spendingTrend(normalizedMonthYear),
      queryFn: () => this.fetchGroupSpendingTrendFromBackend(normalizedMonthYear),
      staleTime: 0,
    });
  }

  /** Budgeted and sorted-transaction totals per bucket per month (reports feature). */
  groupMonthlyReportQueryOptions(fromMonthYear: string, toMonthYear: string) {
    return queryOptions({
      queryKey: groupQueryKeys.monthlyReport(fromMonthYear, toMonthYear),
      queryFn: () =>
        firstValueFrom(
          this.backend.request<GroupMonthlyReport>({
            type: 'get',
            apiRoute: `group/monthly-report?fromMonthYear=${encodeURIComponent(fromMonthYear)}&toMonthYear=${encodeURIComponent(toMonthYear)}`,
          }),
        ),
      staleTime: 60_000,
      // Keep the current charts on screen while a new date range loads.
      placeholderData: keepPreviousData,
    });
  }

  /** Recurring bills still expected in a month, by budget (reports feature). */
  groupMonthlyPaceQueryOptions(monthYear: string) {
    return queryOptions({
      queryKey: groupQueryKeys.monthlyPace(monthYear),
      queryFn: () =>
        firstValueFrom(
          this.backend.request<GroupMonthlyPace>({
            type: 'get',
            apiRoute: `group/monthly-pace?monthYear=${encodeURIComponent(monthYear)}`,
          }),
        ),
      staleTime: 60_000,
    });
  }

  setGroupPage(monthYear: string, groupPage: GroupPage) {
    const normalizedMonthYear = this.normalizeMonthYear(monthYear);
    this.queryClient.setQueryData(groupQueryKeys.page(normalizedMonthYear), groupPage);
  }

  updateBucketInGroupPage(bucket: Bucket): void {
    const normalizedMonthYear = this.normalizeMonthYear(bucket.monthYear);
    this.queryClient.setQueryData<GroupPage>(groupQueryKeys.page(normalizedMonthYear), page => {
      if (!page) return page;

      let changed = false;
      const data = page.data.map(group => {
        const existingBucket = group.buckets.find(existing => existing.id === bucket.id);
        const nextBuckets = group.buckets.filter(existing => existing.id !== bucket.id);

        if (existingBucket) changed = true;

        if (group.id !== bucket.groupId) return existingBucket ? { ...group, buckets: nextBuckets } : group;

        const nextBucket = existingBucket ? { ...existingBucket, ...bucket } : bucket;
        changed = true;
        return {
          ...group,
          buckets: [...nextBuckets, nextBucket].sort((a, b) => a.sortIndex - b.sortIndex),
        };
      });

      return changed ? { ...page, data } : page;
    });
  }

  invalidateGroupPage(monthYear = this.month.monthYearFromDate(), refreshIfLoaded: boolean = true): void {
    const normalizedMonthYear = this.normalizeMonthYear(monthYear);
    const queryKey = groupQueryKeys.page(normalizedMonthYear);
    const loaded = !!this.queryClient.getQueryData<GroupPage>(queryKey);

    void this.queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
    if (refreshIfLoaded && loaded) this.refreshGroupInBackground(normalizedMonthYear);
    this.invalidateReports();
  }

  /**
   * Reports span many months and include bills still due, so any change that
   * refreshes a group page can move them. Mounted reports refetch; others wait.
   */
  invalidateReports(): void {
    void this.queryClient.invalidateQueries({ queryKey: groupQueryKeys.monthlyReports() });
    void this.queryClient.invalidateQueries({ queryKey: groupQueryKeys.monthlyPaces() });
  }

  invalidateCurrentGroupPage(refreshIfLoaded: boolean = true): void {
    this.invalidateGroupPage(this.month.currentMonthYear(), refreshIfLoaded);
  }

  invalidateGroupPages(monthYears: Array<string | null | undefined>, refreshIfLoaded: boolean = true): void {
    Array.from(new Set(monthYears.filter((monthYear): monthYear is string => !!monthYear))).forEach(monthYear =>
      this.invalidateGroupPage(monthYear, refreshIfLoaded),
    );
  }

  invalidateLoadedGroupPages(refreshIfLoaded: boolean = false): void {
    this.queryClient
      .getQueryCache()
      .findAll({ queryKey: groupQueryKeys.all })
      .forEach(query => {
        const monthYear = query.queryKey[2];
        if (typeof monthYear === 'string') this.invalidateGroupPage(monthYear, refreshIfLoaded);
      });
  }

  ignoreIncomeMismatch$() {
    return this.ignoreIncomeMismatchRequest$.pipe(
      switchMap(() => this.invalidateAndRefreshGroupPage$(this.month.currentMonthYear())),
    );
  }

  bulkEditBudgetsMutationOptions() {
    return mutationOptions({
      mutationKey: ['budgets', 'bulk-edit'],
      mutationFn: (data: BudgetBulkEditRequest) => this.bulkEditBudgets(data),
      onSuccess: (groupPage, data) => {
        this.setGroupPage(data.monthYear, groupPage);
        this.invalidateReports();
      },
    });
  }

  createGroupMutationOptions() {
    return mutationOptions({
      mutationKey: ['group', 'create'],
      mutationFn: ({ name }: GroupNameMutationVariables) => this.createGroup(name),
      onSuccess: (_response, { monthYear }) => this.refreshGroupPageAfterWrite(monthYear),
    });
  }

  deleteGroupMutationOptions() {
    return mutationOptions({
      mutationKey: ['group', 'delete'],
      mutationFn: ({ id }: DeleteGroupMutationVariables) => this.deleteGroup(id),
      onSuccess: (_response, { monthYear }) => this.refreshGroupPageAfterWrite(monthYear),
    });
  }

  updateMonthlyBudgetMutationOptions() {
    return mutationOptions({
      mutationKey: ['group', 'monthly-budget'],
      mutationFn: ({ budget, monthYear }: UpdateMonthlyBudgetMutationVariables) =>
        this.updateMonthlyBudget(budget, monthYear ?? undefined),
      onSuccess: ({ monthYear }) => this.refreshGroupPageAfterWrite(monthYear),
    });
  }

  bulkEditBudgets(data: BudgetBulkEditRequest): Promise<GroupPage> {
    return firstValueFrom(
      this.backend.request<GroupPage, BudgetBulkEditRequest>({
        type: 'post',
        apiRoute: 'budgets/bulk-edit',
        data,
        timeoutMillis: 15000,
      }),
    );
  }

  createGroup(name: string): Promise<void> {
    return firstValueFrom(
      this.backend.request<void, CreateGroupRequest>({
        type: 'post',
        apiRoute: 'group',
        data: { name },
      }),
    );
  }

  deleteGroup(id: string): Promise<void> {
    return firstValueFrom(this.backend.request<void>({ type: 'delete', apiRoute: `group/${id}` }));
  }

  updateMonthlyBudget(budget: number, monthYear = this.month.currentMonthYear()): Promise<AccountBudget> {
    return firstValueFrom(
      this.backend.request<AccountBudget, UpdateAccountBudgetRequest>({
        type: 'update',
        apiRoute: `account/budget/${this.normalizeMonthYear(monthYear)}`,
        data: {
          budget,
        },
      }),
    );
  }

  updateMonthlyBudget$(budget: number) {
    return this.month.currentMonthYear$.pipe(
      take(1),
      switchMap(monthYear => from(this.updateMonthlyBudget(budget, monthYear))),
      switchMap(({ monthYear }) => this.invalidateAndRefreshGroupPage$(monthYear)),
    );
  }

  invalidateAndRefreshGroupPage$(monthYear: string): Observable<GroupPage> {
    const normalizedMonthYear = this.normalizeMonthYear(monthYear);
    const queryKey = groupQueryKeys.page(normalizedMonthYear);
    this.invalidateReports();
    return from(
      this.queryClient
        .invalidateQueries({ queryKey, refetchType: 'none' })
        .then(() => this.queryClient.fetchQuery(this.groupPageQueryOptions(normalizedMonthYear))),
    );
  }

  private refreshGroupPageAfterWrite(monthYear?: string | null): Promise<GroupPage> {
    return firstValueFrom(this.invalidateAndRefreshGroupPage$(this.normalizeMonthYear(monthYear ?? '')));
  }

  private refreshGroupInBackground(monthYear: string): void {
    void this.queryClient.prefetchQuery(this.groupPageQueryOptions(monthYear));
  }

  private normalizeMonthYear(monthYear: string): string {
    return monthYear || this.month.monthYearFromDate();
  }

  groupPageQueryOptions(monthYear = this.month.monthYearFromDate()) {
    const normalizedMonthYear = this.normalizeMonthYear(monthYear);
    return queryOptions({
      queryKey: groupQueryKeys.page(normalizedMonthYear),
      queryFn: () => this.fetchGroupPageFromBackend(normalizedMonthYear),
      staleTime: 0,
    });
  }

  private fetchGroupPageFromBackend(monthYear: string): Promise<GroupPage> {
    return firstValueFrom(
      this.backend.request<GroupPage & GroupList>({
        type: 'get',
        apiRoute: monthYear !== this.month.monthYearFromDate() ? `group/${monthYear}` : 'group',
      }),
    );
  }

  private fetchGroupSpendingTrendFromBackend(monthYear: string): Promise<GroupSpendingTrendPoint[]> {
    return firstValueFrom(
      this.backend
        .request<GroupSpendingTrend>({
          type: 'get',
          apiRoute: `group/spending-trend?monthYear=${encodeURIComponent(monthYear)}`,
        })
        .pipe(map(response => response.data)),
    );
  }
}
